import { logEvent } from "./analytics"
import { isStaleChunkError } from "./staleChunk"
// ── Error monitoring ──────────────────────────────────────────────────────────
// Sentry, wired but DORMANT until a DSN exists. It's a DYNAMIC import gated on
// VITE_SENTRY_DSN, so with no DSN the Sentry chunk is never fetched and there is
// zero bundle/runtime cost — the app behaves exactly as before. To turn it on:
// create a free Sentry project, then set VITE_SENTRY_DSN in the Netlify build env
// (a rebuild bakes it in). initMonitoring() runs once at boot; reportError() is
// called by the ErrorBoundary and any catch that wants to surface a real failure.
// WHY @sentry/browser AND NAMED IMPORTS (perf audit 2026-08-01)
// This used `const S = await import('@sentry/react')` and kept the whole module
// namespace. Holding a namespace defeats tree-shaking completely — the bundler can't
// know which properties get read off `S`, so it must keep ALL of them. Measured
// result: a 461 KB / 149 KB gz chunk, larger than the app's own entry chunk, carrying
// Session Replay (rrweb), the Feedback widget and BrowserTracing — none of which this
// app uses or configures.
// It's @sentry/browser, not /react, because nothing here touches a React-specific
// Sentry API: we call init + captureException, and the ErrorBoundary is our own
// (components/ErrorBoundary.jsx). Destructuring named exports lets Rollup drop the rest.
let capture = null

export async function initMonitoring() {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  // Production builds only. The dev server (vite, MODE=development) surfaces every error
  // live in the console anyway, and reporting from it just spams Sentry/email with noise
  // like Fast-Refresh "rendered fewer hooks" artifacts fired while editing a component's
  // hooks mid-session (NEXTEP-1K, 2026-08-11 — culprit @react-refresh, dev-only, not a
  // real bug). Sentry earns its keep on the deployed app, not on localhost.
  if (!dsn || capture || !import.meta.env.PROD) return
  try {
    const { init, captureException } = await import('@sentry/browser')
    init({
      dsn,
      environment: import.meta.env.MODE,
      // Errors only — no performance tracing, so there's no sampling overhead.
      tracesSampleRate: 0,
      // Noise the browser throws that isn't actionable.
      ignoreErrors: [
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
        'Non-Error promise rejection captured',
      ],
    })
    capture = captureException
  } catch { /* monitoring must never break the app */ }
}

export function reportError(error, context) {
  // First-party crash record (2026-09-12). Sentry is dormant (no DSN), so until now a
  // production render crash left NO trace anywhere we can query — the friction panel
  // could show a rage tap but never the crash that caused it. Fire-and-forget into
  // app_events with the route + a short message; the admin Friction view reads it.
  try {
    const msg = String(error?.message || error || '').slice(0, 160)
    // Stale-chunk failures (a lazy import after a deploy) already recover by reloading;
    // one still reached the War Room as a crash on /help (2026-10-27, "reading 'default'").
    // The same test staleChunk.js uses to decide to reload decides to stay quiet here.
    if (msg && !isStaleChunkError(error) && !/Loading chunk|ChunkLoadError|MIME type/i.test(msg)) {
      logEvent('ui_crash', { route: typeof location !== 'undefined' ? location.pathname : '', msg, src: context?.source || context?.boundary || '' })
    }
  } catch {}
  if (!capture) return
  try { capture(error, context ? { extra: context } : undefined) } catch {}
}
